import React, { useCallback } from 'react';
import Card from '@/components/UI/Card';
import FormSwitch from '@/components/UI/FormSwitch';
import { DeleteButton } from '@/components/UI';
import { trpc } from '@/api/trpc.ts';
import { useQueryClient } from '@tanstack/react-query';
import { useTrpcMutation } from '@/api/useTrpcMutation';
import type { OptimisticPortfolioSettings } from './types';

interface NotificationsCardProps {
    portfolioId: string | undefined;
    optimisticSettings: OptimisticPortfolioSettings;
    setOptimisticSettings: React.Dispatch<React.SetStateAction<OptimisticPortfolioSettings>>;
    pendingMutations: Record<string, boolean>;
    setPendingMutations: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
    showResetButton: boolean;
    onResetClick: () => void;
}

const NotificationsCard: React.FC<NotificationsCardProps> = ({
    portfolioId,
    optimisticSettings,
    setOptimisticSettings,
    pendingMutations,
    setPendingMutations,
    showResetButton,
    onResetClick
}) => {
    const queryClient = useQueryClient();

    // Mutation for toggling buy notifications
    const toggleBuyNotificationsMutation = useTrpcMutation(
        trpc.user.savePortfolioSetting.mutationOptions({
            onSuccess: () => {
                void queryClient.invalidateQueries({
                    queryKey: trpc.user.fetchPortfolio.queryOptions({ id: parseInt(portfolioId || '0'), shouldFetchTrades: true }).queryKey
                });

                // Clear pending mutation flag
                setPendingMutations((prev) => ({
                    ...prev,
                    buyNotifications: false
                }));
            },
            onError: () => {
                // Revert optimistic update on error
                setOptimisticSettings((prev) => ({
                    ...prev,
                    buyNotifications: !prev.buyNotifications
                }));

                // Clear pending mutation flag
                setPendingMutations((prev) => ({
                    ...prev,
                    buyNotifications: false
                }));
            }
        }),
        {
            successMessage: 'Buy notifications setting updated successfully',
            fallbackErrorMessage: 'Failed to update buy notifications setting'
        }
    );

    const handleToggleBuyNotifications = useCallback(() => {
        // Get current value (use optimistic state)
        const currentValue = optimisticSettings.buyNotifications;

        // Prevent double-clicks
        if (pendingMutations['buyNotifications']) {
            return;
        }

        // Set pending mutation flag
        setPendingMutations((prev) => ({
            ...prev,
            buyNotifications: true
        }));

        // Update optimistic UI immediately
        setOptimisticSettings((prev) => ({
            ...prev,
            buyNotifications: !currentValue
        }));

        // Send request to server
        void toggleBuyNotificationsMutation.mutateAsync({
            portfolioId: parseInt(portfolioId || '0'),
            settingType: 'buyNotifications',
            value: { value: !currentValue }
        });
    }, [
        portfolioId,
        optimisticSettings.buyNotifications,
        toggleBuyNotificationsMutation,
        pendingMutations,
        setPendingMutations,
        setOptimisticSettings
    ]);

    // Mutation for toggling sell notifications
    const toggleSellNotificationsMutation = useTrpcMutation(
        trpc.user.savePortfolioSetting.mutationOptions({
            onSuccess: () => {
                void queryClient.invalidateQueries({
                    queryKey: trpc.user.fetchPortfolio.queryOptions({ id: parseInt(portfolioId || '0'), shouldFetchTrades: true }).queryKey
                });

                // Clear pending mutation flag
                setPendingMutations((prev) => ({
                    ...prev,
                    sellNotifications: false
                }));
            },
            onError: () => {
                // Revert optimistic update on error
                setOptimisticSettings((prev) => ({
                    ...prev,
                    sellNotifications: !prev.sellNotifications
                }));

                // Clear pending mutation flag
                setPendingMutations((prev) => ({
                    ...prev,
                    sellNotifications: false
                }));
            }
        }),
        {
            successMessage: 'Sell notifications setting updated successfully',
            fallbackErrorMessage: 'Failed to update sell notifications setting'
        }
    );

    const handleToggleSellNotifications = useCallback(() => {
        // Get current value (use optimistic state)
        const currentValue = optimisticSettings.sellNotifications;

        // Prevent double-clicks
        if (pendingMutations['sellNotifications']) {
            return;
        }

        // Set pending mutation flag
        setPendingMutations((prev) => ({
            ...prev,
            sellNotifications: true
        }));

        // Update optimistic UI immediately
        setOptimisticSettings((prev) => ({
            ...prev,
            sellNotifications: !currentValue
        }));

        // Send request to server
        void toggleSellNotificationsMutation.mutateAsync({
            portfolioId: parseInt(portfolioId || '0'),
            settingType: 'sellNotifications',
            value: { value: !currentValue }
        });
    }, [
        portfolioId,
        optimisticSettings.sellNotifications,
        toggleSellNotificationsMutation,
        pendingMutations,
        setPendingMutations,
        setOptimisticSettings
    ]);

    return (
        <Card className="mb-4" title="Notifications">
            <div className="space-y-3">
                <FormSwitch
                    checked={optimisticSettings.sellNotifications}
                    className="	mr-2 "
                    disabled={pendingMutations['sellNotifications']}
                    id="sellNotificationsCheck"
                    label="Sale trade"
                    onChange={handleToggleSellNotifications}
                />
                <FormSwitch
                    checked={optimisticSettings.buyNotifications}
                    disabled={pendingMutations['buyNotifications']}
                    id="buyNotificationsCheck"
                    label="Buy trade"
                    onChange={handleToggleBuyNotifications}
                />
            </div>

            {showResetButton && (
                <div className="mt-6 text-center">
                    <DeleteButton text="Reset Portfolio" onClick={onResetClick} />
                </div>
            )}
        </Card>
    );
};

export default NotificationsCard;
